import { z } from "zod";

export * from "./localization";

export const MAX_FIT_SOURCE_BYTES = 256 * 1024;
export const MAX_KML_SOURCE_BYTES = 20 * 1024 * 1024;

export const localeSchema = z.enum(["en", "zh-CN", "fr", "de", "it", "nl", "es"]);
export const unitsSchema = z.enum(["metric", "imperial"]);
export const sportSchema = z.enum(["cycling", "running", "hiking"]);

function isIanaTimezone(value: string): boolean {
  try {
    new Intl.DateTimeFormat("en", { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

export const preferencesSchema = z.object({
  locale: localeSchema,
  units: unitsSchema,
  timezone: z.string().min(1).max(100).refine(isIanaTimezone, "Invalid IANA timezone"),
  weekStart: z.enum(["monday", "sunday"]),
  theme: z.enum(["system", "dark", "light"]),
});

export const eligibilitySchema = z.object({
  policyVersion: z.literal("eligibility-v1").default("eligibility-v1"),
});

export const enrollmentSchema = z.object({
  evidenceId: z.uuid(),
  name: z.string().trim().min(1).max(100),
  email: z.email().max(320),
  password: z.string().min(12).max(128),
  locale: localeSchema.default("en"),
});

export const createImportSchema = z
  .object({
    declaredType: z.enum(["fit", "kml"]),
    byteSize: z.number().int().positive().max(MAX_KML_SOURCE_BYTES),
    displayName: z.string().min(1).max(255).optional(),
    batchId: z.uuid().optional(),
  })
  .superRefine(({ byteSize, declaredType }, context) => {
    if (declaredType === "fit" && byteSize > MAX_FIT_SOURCE_BYTES) {
      context.addIssue({
        code: "too_big",
        maximum: MAX_FIT_SOURCE_BYTES,
        origin: "number",
        inclusive: true,
        path: ["byteSize"],
        message: "FIT sources are limited to 256 KiB by the measured production envelope.",
      });
    }
  });

const boundedDateRange = z
  .object({
    start: z.iso.date(),
    end: z.iso.date(),
  })
  .refine(({ start, end }) => {
    const duration = Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`);
    return duration >= 0 && duration <= 366 * 86_400_000;
  }, "Date range must be ordered and no longer than 366 days");

export const createReportSchema = boundedDateRange.and(
  z.object({
    sports: z.array(sportSchema).min(1).max(3),
    tags: z.array(z.string().min(1).max(50)).max(20).default([]),
    locale: localeSchema,
    units: unitsSchema,
    privacyMode: z.enum(["private", "mapless", "redacted"]),
    sections: z
      .array(
        z.enum([
          "summary",
          "volume",
          "terrain",
          "sensors",
          "records",
          "privacy",
          "actions",
          "limitations",
        ]),
      )
      .min(1),
  }),
);

export const createShareSchema = z
  .object({
    activityId: z.uuid(),
    snapshotId: z.uuid(),
    mode: z.enum(["mapless", "route"]).default("mapless"),
    expiresInDays: z.number().int().min(1).max(30).default(7),
    verificationEmail: z.email().optional(),
    allowDownload: z.boolean().default(false),
    highRiskAcknowledged: z.boolean().default(false),
  })
  .refine((value) => value.mode !== "route" || value.highRiskAcknowledged, {
    message: "Full route sharing requires an explicit acknowledgement",
    path: ["highRiskAcknowledged"],
  });

export const activityPatchSchema = z
  .object({
    title: z.string().min(1).max(160).optional(),
    notes: z.string().max(5_000).optional(),
    equipment: z.string().max(160).nullable().optional(),
    tags: z.array(z.string().min(1).max(50)).max(20).optional(),
    archived: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, "At least one field is required");

export const redactionPolicySchema = z.object({
  mode: z.enum(["mapless", "route"]),
  trimStartMeters: z.number().min(0).max(10_000),
  trimEndMeters: z.number().min(0).max(10_000),
  trimStartSeconds: z.number().min(0).max(86_400).default(0),
  trimEndSeconds: z.number().min(0).max(86_400).default(0),
  zones: z
    .array(
      z.object({
        latitude: z.number().min(-90).max(90),
        longitude: z.number().min(-180).max(180),
        radiusMeters: z.number().min(100).max(10_000),
      }),
    )
    .max(20)
    .default([]),
  zoneIds: z.array(z.uuid()).max(20).default([]),
  includedMetricKeys: z.array(
    z.enum([
      "distance",
      "duration",
      "speed",
      "ascent",
      "heartRate",
      "cadence",
      "power",
      "temperature",
      "calories",
    ]),
  ),
  locale: localeSchema,
  units: unitsSchema,
});

export const privacyZoneSchema = z.object({
  label: z.string().trim().min(1).max(80),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  radiusMeters: z.number().min(100).max(10_000),
});

export const correctionSchema = z.object({
  field: z.enum(["sport", "timezone", "distance", "duration", "ascent"]),
  value: z.union([sportSchema, z.string().max(100), z.number().finite().nonnegative()]),
  reason: z.string().trim().min(3).max(500),
});

export const duplicateResolutionSchema = z
  .object({
    resolution: z.enum(["same_activity", "distinct_activities", "keep_both_count_one"]),
    countActivityId: z.uuid().optional(),
  })
  .refine(
    (value) => value.resolution !== "keep_both_count_one" || value.countActivityId !== undefined,
    {
      message: "Choose which activity remains countable",
      path: ["countActivityId"],
    },
  );

export const bulkActivitySchema = z
  .object({
    activityIds: z.array(z.uuid()).min(1).max(100),
    action: z.enum(["archive", "restore", "tag"]),
    tag: z.string().trim().min(1).max(50).optional(),
  })
  .refine((value) => value.action !== "tag" || value.tag !== undefined, {
    message: "A tag is required",
    path: ["tag"],
  });

export const comparisonSchema = z
  .object({
    activityIds: z.array(z.uuid()).min(1).max(100).optional(),
    start: z.iso.date().optional(),
    end: z.iso.date().optional(),
  })
  .refine(
    (value) =>
      value.activityIds !== undefined || (value.start !== undefined && value.end !== undefined),
    {
      message: "Select activities or a date range",
    },
  )
  .refine(
    ({ start, end }) => {
      if (start === undefined || end === undefined) return true;
      const duration = Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`);
      return duration >= 0 && duration <= 366 * 86_400_000;
    },
    { message: "Date range must be ordered and no longer than 366 days" },
  );

export const consentSchema = z.object({
  purpose: z.enum(["terms", "core", "ai", "photoExif", "marketing"]),
  granted: z.boolean(),
  policyVersion: z.string().min(1).max(50),
  noticeVersion: z.string().min(1).max(50),
  locale: localeSchema,
});

export const activityStoryRequestSchema = z.object({
  locale: localeSchema,
});

export const activityEvidenceReferenceSchema = z
  .string()
  .regex(/^[a-z][a-z0-9]*(?:[._-][a-z0-9]+)*$/u)
  .max(100);

const activityAnalysisCategorySchema = z.enum([
  "route_load",
  "route_structure",
  "effort",
  "sensors",
  "quality",
  "source_integrity",
  "privacy",
]);

export const activityStoryNarrativeSchema = z.object({
  headline: z.string().min(1).max(120),
  overview: z.string().min(1).max(560),
  sections: z
    .array(
      z.object({
        category: activityAnalysisCategorySchema,
        title: z.string().min(1).max(90),
        body: z.string().min(1).max(520),
        confidence: z.enum(["high", "medium", "low"]),
        findings: z
          .array(
            z.object({
              kind: z.enum(["observation", "relationship", "caveat"]),
              text: z.string().min(1).max(240),
            }),
          )
          .min(1)
          .max(3),
        evidenceRefs: z.array(activityEvidenceReferenceSchema).min(1).max(4),
      }),
    )
    .min(2)
    .max(6),
  recommendations: z
    .array(
      z.object({
        category: activityAnalysisCategorySchema,
        title: z.string().min(1).max(90),
        body: z.string().min(1).max(420),
        steps: z.array(z.string().min(1).max(180)).min(1).max(3),
        evidenceRefs: z.array(activityEvidenceReferenceSchema).min(1).max(3),
      }),
    )
    .min(2)
    .max(8),
  limitations: z.array(z.string().min(1).max(240)).min(1).max(6),
  suggestedQuestions: z.array(z.string().min(1).max(160)).min(2).max(4),
});

export const activityChatRequestSchema = z
  .object({
    locale: localeSchema,
    messages: z
      .array(
        z.object({
          role: z.enum(["user", "assistant"]),
          content: z.string().trim().min(1).max(1_200),
        }),
      )
      .min(1)
      .max(12),
  })
  .refine((value) => value.messages.at(-1)?.role === "user", {
    message: "The last chat message must come from the user",
    path: ["messages"],
  });

export const activityChatResponseSchema = z.object({
  answer: z.string().min(1).max(2_400),
  evidenceRefs: z.array(activityEvidenceReferenceSchema).max(5),
  boundary: z.string().min(1).max(300).nullable(),
  suggestedQuestions: z.array(z.string().min(1).max(160)).max(3),
});

export interface ApiMeta {
  requestId: string;
  schemaVersion: 1;
  nextCursor: string | null;
}

export interface ApiEnvelope<T> {
  data: T;
  meta: ApiMeta;
  warnings: string[];
}

export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail: string;
  code: string;
  requestId: string;
  errors?: Array<{ path: string; message: string }>;
}

export type Preferences = z.infer<typeof preferencesSchema>;
export type CreateImport = z.infer<typeof createImportSchema>;
export type CreateReport = z.infer<typeof createReportSchema>;
export type CreateShare = z.infer<typeof createShareSchema>;
export type ActivityStoryNarrative = z.infer<typeof activityStoryNarrativeSchema>;
export type ActivityChatRequest = z.infer<typeof activityChatRequestSchema>;
export type ActivityChatResponse = z.infer<typeof activityChatResponseSchema>;
